import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type {
  AssessmentRegisterResponse,
  AssessmentRegisterResponseRow,
} from '@risksmart-app/trpc/src/types';
import type { GetAssessmentsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

const mapTrpcAssessmentToGraphQL = (
  assessment: AssessmentRegisterResponseRow
): GetAssessmentsQuery['assessment'][number] => {
  return {
    ...assessment,
    assessedItems: assessment.assessmentResults,
  };
};

/**
 * Maps TRPC assessment data to match the GraphQL query structure
 */
export const mapTrpcAssessmentsToGraphQL = (
  trpcData: AssessmentRegisterResponse
): GetAssessmentsQuery => {
  return {
    assessment: trpcData.assessment.map((assessment) =>
      mapTrpcAssessmentToGraphQL(assessment)
    ),
  };
};

export const useGetAssessmentsRegister = (skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.assessment.register.queryOptions(),
    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData ? mapTrpcAssessmentsToGraphQL(trpcData) : undefined;

  return {
    loading: trpcLoading,
    data,
    error: trpcError,
    refetch: () => trpcRefetch(),
  };
};
